// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export function isEntityLookupParamsModel(source: any): source is IEntityLookupParams {
    // Method Implementation: simple
    return source.modelName === 'EntityLookupParams';
}

export function isQuestionDefaultModel(source: any): source is IQuestionDefault {
    // Method Implementation: simple
    return source.modelName === 'QuestionDefault';
}

export function isTelemetryContextModel(source: any): source is ITelemetryContext {
    // Method Implementation: simple
    return source.modelName === 'TelemetryContext';
}

export interface EntityLookupParamsZodSchemaShape {
    application?: string;

    businessUnit?: string;

    model?: string;

    entityType?: string;
}

export interface QuestionDefaultZodSchemaShape {
    name?: string;

    value?: string;

    restrict?: boolean;

    userAssigned?: boolean;
}

export interface TelemetryContextZodSchemaShape {
    businessUnit?: string;

    office?: string;

    features?: any;
}

// Artifact Implementation: artifacts/zodSchemaType
export const EntityLookupParamsZodSchema: Zod.ZodSchema<EntityLookupParamsZodSchemaShape> = Zod.lazy(() => Zod.object({
    application: Zod.string().optional(),
    businessUnit: Zod.string().optional(),
    model: Zod.string().optional(),
    entityType: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionDefaultZodSchema: Zod.ZodSchema<QuestionDefaultZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    restrict: Zod.boolean().optional(),
    userAssigned: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const TelemetryContextZodSchema: Zod.ZodSchema<TelemetryContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    businessUnit: Zod.string().optional(),
    office: Zod.string().optional(),
    features: Zod.any().optional()
}).nonstrict());

export interface IEntityLookupParams extends Wellspring.IModel {
    readonly modelName: string;

    readonly application: string;

    readonly businessUnit: string;

    readonly model: string;

    readonly entityType: string;

    readonly isApplicationDefined: boolean;

    readonly isBusinessUnitDefined: boolean;

    readonly isModelDefined: boolean;

    readonly isEntityTypeDefined: boolean;
}

export interface IQuestionDefault extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly restrict?: boolean;

    readonly userAssigned?: boolean;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isRestrictDefined: boolean;

    readonly isUserAssignedDefined: boolean;
}

export interface ITelemetryContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly businessUnit: string;

    readonly office: string;

    readonly features: Record<string, string>;

    readonly isBusinessUnitDefined: boolean;

    readonly isOfficeDefined: boolean;

    readonly isFeaturesDefined: boolean;
}

export class EntityLookupParams implements IEntityLookupParams {
    public static readonly modelName: string = 'EntityLookupParams';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'application', 'businessUnit', 'model', 'entityType' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityLookupParams.modelName;
    }

    private _application: string;

    private _businessUnit: string;

    private _model: string;

    private _entityType: string;

    public get application(): string {
        // Method Implementation: simple
        return this._application;
    }

    public get businessUnit(): string {
        // Method Implementation: simple
        return this._businessUnit;
    }

    public get model(): string {
        // Method Implementation: simple
        return this._model;
    }

    public get entityType(): string {
        // Method Implementation: simple
        return this._entityType;
    }

    public set application(value: string) {
        // Method Implementation: simple
        this._application = value;
        this._isApplicationDefined = true;
    }

    public set businessUnit(value: string) {
        // Method Implementation: simple
        this._businessUnit = value;
        this._isBusinessUnitDefined = true;
    }

    public set model(value: string) {
        // Method Implementation: simple
        this._model = value;
        this._isModelDefined = true;
    }

    public set entityType(value: string) {
        // Method Implementation: simple
        this._entityType = value;
        this._isEntityTypeDefined = true;
    }

    private _isApplicationDefined: boolean;

    private _isBusinessUnitDefined: boolean;

    private _isModelDefined: boolean;

    private _isEntityTypeDefined: boolean;

    public get isApplicationDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicationDefined;
    }

    public get isBusinessUnitDefined(): boolean {
        // Method Implementation: simple
        return this._isBusinessUnitDefined;
    }

    public get isModelDefined(): boolean {
        // Method Implementation: simple
        return this._isModelDefined;
    }

    public get isEntityTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityTypeDefined;
    }

    public constructor();
    public constructor(source: IEntityLookupParams);
    public constructor(source: Partial<Pick<EntityLookupParams, 'application' | 'businessUnit' | 'model' | 'entityType'>>);
    public constructor(source?: IEntityLookupParams | Partial<Pick<EntityLookupParams, 'application' | 'businessUnit' | 'model' | 'entityType'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityLookupParamsModel(source)) {
                if (source.isApplicationDefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.isBusinessUnitDefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.isModelDefined) {
                    this._model = source.model;
                    this._isModelDefined = true;
                }
                else {
                    this._model = '';
                    this._isModelDefined = false;
                }
                
                if (source.isEntityTypeDefined) {
                    this._entityType = source.entityType;
                    this._isEntityTypeDefined = true;
                }
                else {
                    this._entityType = '';
                    this._isEntityTypeDefined = false;
                }
            }
            else {
                if (source.application !== undefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = '';
                    this._isApplicationDefined = false;
                }
                
                if (source.businessUnit !== undefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.model !== undefined) {
                    this._model = source.model;
                    this._isModelDefined = true;
                }
                else {
                    this._model = '';
                    this._isModelDefined = false;
                }
                
                if (source.entityType !== undefined) {
                    this._entityType = source.entityType;
                    this._isEntityTypeDefined = true;
                }
                else {
                    this._entityType = '';
                    this._isEntityTypeDefined = false;
                }
            }
        }
        else {
            this._application = '';
            this._isApplicationDefined = false;
        
            this._businessUnit = '';
            this._isBusinessUnitDefined = false;
        
            this._model = '';
            this._isModelDefined = false;
        
            this._entityType = '';
            this._isEntityTypeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityLookupParams.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isApplicationDefined && this.application !== undefined)
            definedProperties.push('application');
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined)
            definedProperties.push('businessUnit');
        if (this.isModelDefined && this.model !== undefined)
            definedProperties.push('model');
        if (this.isEntityTypeDefined && this.entityType !== undefined)
            definedProperties.push('entityType');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'application':
                return this.application;
            case 'businessUnit':
                return this.businessUnit;
            case 'model':
                return this.model;
            case 'entityType':
                return this.entityType;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isApplicationDefined && this.application !== undefined) {
            if (this.application !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.application);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'null'));
            }
        }
        
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            if (this.businessUnit !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.businessUnit);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'null'));
            }
        }
        
        if (this.isModelDefined && this.model !== undefined) {
            if (this.model !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('model', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.model);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('model', 'null'));
            }
        }
        
        if (this.isEntityTypeDefined && this.entityType !== undefined) {
            if (this.entityType !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityType', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityType);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityType', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'application':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.application = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'application' cannot be null`);
                    this.application = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'application'`);
                }
        
                return true;
            case 'businessUnit':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.businessUnit = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'businessUnit' cannot be null`);
                    this.businessUnit = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'businessUnit'`);
                }
        
                return true;
            case 'model':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.model = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'model' cannot be null`);
                    this.model = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'model'`);
                }
        
                return true;
            case 'entityType':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityType = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityType' cannot be null`);
                    this.entityType = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityType'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // application
        if (this.isApplicationDefined && this.application !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.application === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.application);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // businessUnit
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.businessUnit === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.businessUnit);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // model
        if (this.isModelDefined && this.model !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.model === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.model);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // entityType
        if (this.isEntityTypeDefined && this.entityType !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityType === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityType);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // application
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.application = <any>null;
                    break;
                case 1:
                    this.application = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // businessUnit
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.businessUnit = <any>null;
                    break;
                case 1:
                    this.businessUnit = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // model
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.model = <any>null;
                    break;
                case 1:
                    this.model = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // entityType
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityType = <any>null;
                    break;
                case 1:
                    this.entityType = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): EntityLookupParams {
        // Method Implementation: simple
        const schema = EntityLookupParamsZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityLookupParamsZodSchemaShape): EntityLookupParams {
        // Method Implementation: parseZodSchema
        return new EntityLookupParams({
            application: schema.application,
            businessUnit: schema.businessUnit,
            model: schema.model,
            entityType: schema.entityType
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            application: this.isApplicationDefined ? ((this.application === null ? undefined : this.application)) : undefined,
            businessUnit: this.isBusinessUnitDefined ? ((this.businessUnit === null ? undefined : this.businessUnit)) : undefined,
            model: this.isModelDefined ? ((this.model === null ? undefined : this.model)) : undefined,
            entityType: this.isEntityTypeDefined ? ((this.entityType === null ? undefined : this.entityType)) : undefined,
        };
    }

    public clone(): EntityLookupParams {
        // Method Implementation: clone
        return new EntityLookupParams({
            application: this.isApplicationDefined ? this.application : undefined,
            businessUnit: this.isBusinessUnitDefined ? this.businessUnit : undefined,
            model: this.isModelDefined ? this.model : undefined,
            entityType: this.isEntityTypeDefined ? this.entityType : undefined
        });
    }
}

export class QuestionDefault implements IQuestionDefault {
    public static readonly modelName: string = 'QuestionDefault';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value', 'restrict', 'userAssigned' ];

    public get modelName(): string {
        // Method Implementation: simple
        return QuestionDefault.modelName;
    }

    private _name: string;

    private _value: string;

    private _restrict?: boolean;

    private _userAssigned?: boolean;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get restrict(): boolean | undefined {
        // Method Implementation: simple
        return this._restrict;
    }

    public get userAssigned(): boolean | undefined {
        // Method Implementation: simple
        return this._userAssigned;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set restrict(value: boolean | undefined) {
        // Method Implementation: simple
        this._restrict = value;
        this._isRestrictDefined = true;
    }

    public set userAssigned(value: boolean | undefined) {
        // Method Implementation: simple
        this._userAssigned = value;
        this._isUserAssignedDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    private _isRestrictDefined: boolean;

    private _isUserAssignedDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isRestrictDefined(): boolean {
        // Method Implementation: simple
        return this._isRestrictDefined;
    }

    public get isUserAssignedDefined(): boolean {
        // Method Implementation: simple
        return this._isUserAssignedDefined;
    }

    public constructor();
    public constructor(source: IQuestionDefault);
    public constructor(source: Partial<Pick<QuestionDefault, 'name' | 'value' | 'restrict' | 'userAssigned'>>);
    public constructor(source?: IQuestionDefault | Partial<Pick<QuestionDefault, 'name' | 'value' | 'restrict' | 'userAssigned'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionDefaultModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isRestrictDefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.isUserAssignedDefined) {
                    this._userAssigned = source.userAssigned;
                    this._isUserAssignedDefined = true;
                }
                else {
                    this._userAssigned = false;
                    this._isUserAssignedDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.restrict !== undefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.userAssigned !== undefined) {
                    this._userAssigned = source.userAssigned;
                    this._isUserAssignedDefined = true;
                }
                else {
                    this._userAssigned = false;
                    this._isUserAssignedDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        
            this._restrict = false;
            this._isRestrictDefined = false;
        
            this._userAssigned = false;
            this._isUserAssignedDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return QuestionDefault.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isRestrictDefined && this.restrict !== undefined)
            definedProperties.push('restrict');
        if (this.isUserAssignedDefined && this.userAssigned !== undefined)
            definedProperties.push('userAssigned');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            case 'restrict':
                return this.restrict;
            case 'userAssigned':
                return this.userAssigned;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isRestrictDefined && this.restrict !== undefined) {
            if (this.restrict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.restrict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'null'));
            }
        }
        
        if (this.isUserAssignedDefined && this.userAssigned !== undefined) {
            if (this.userAssigned !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('userAssigned', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.userAssigned);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('userAssigned', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'restrict':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.restrict = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'restrict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'restrict'`);
                }
        
                return true;
            case 'userAssigned':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.userAssigned = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'userAssigned' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'userAssigned'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // restrict
        if (this.isRestrictDefined && this.restrict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.restrict);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // userAssigned
        if (this.isUserAssignedDefined && this.userAssigned !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.userAssigned);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // restrict
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.restrict = reader.readBooleanValue();
        }
        
        // userAssigned
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.userAssigned = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): QuestionDefault {
        // Method Implementation: simple
        const schema = QuestionDefaultZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionDefaultZodSchemaShape): QuestionDefault {
        // Method Implementation: parseZodSchema
        return new QuestionDefault({
            name: schema.name,
            value: schema.value,
            restrict: schema.restrict,
            userAssigned: schema.userAssigned
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            restrict: this.isRestrictDefined ? (this.restrict) : undefined,
            userAssigned: this.isUserAssignedDefined ? (this.userAssigned) : undefined,
        };
    }

    public clone(): QuestionDefault {
        // Method Implementation: clone
        return new QuestionDefault({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            restrict: this.isRestrictDefined ? this.restrict : undefined,
            userAssigned: this.isUserAssignedDefined ? this.userAssigned : undefined
        });
    }
}

export class TelemetryContext implements ITelemetryContext {
    public static readonly modelName: string = 'TelemetryContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'businessUnit', 'office', 'features' ];

    public get modelName(): string {
        // Method Implementation: simple
        return TelemetryContext.modelName;
    }

    private _businessUnit: string;

    private _office: string;

    private _features: Record<string, string>;

    public get businessUnit(): string {
        // Method Implementation: simple
        return this._businessUnit;
    }

    public get office(): string {
        // Method Implementation: simple
        return this._office;
    }

    public get features(): Record<string, string> {
        // Method Implementation: simple
        return this._features;
    }

    public set businessUnit(value: string) {
        // Method Implementation: simple
        this._businessUnit = value;
        this._isBusinessUnitDefined = true;
    }

    public set office(value: string) {
        // Method Implementation: simple
        this._office = value;
        this._isOfficeDefined = true;
    }

    public set features(value: Record<string, string>) {
        // Method Implementation: simple
        this._features = value;
        this._isFeaturesDefined = true;
    }

    private _isBusinessUnitDefined: boolean;

    private _isOfficeDefined: boolean;

    private _isFeaturesDefined: boolean;

    public get isBusinessUnitDefined(): boolean {
        // Method Implementation: simple
        return this._isBusinessUnitDefined;
    }

    public get isOfficeDefined(): boolean {
        // Method Implementation: simple
        return this._isOfficeDefined;
    }

    public get isFeaturesDefined(): boolean {
        // Method Implementation: simple
        return this._isFeaturesDefined;
    }

    public constructor();
    public constructor(source: ITelemetryContext);
    public constructor(source: Partial<Pick<TelemetryContext, 'businessUnit' | 'office' | 'features'>>);
    public constructor(source?: ITelemetryContext | Partial<Pick<TelemetryContext, 'businessUnit' | 'office' | 'features'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isTelemetryContextModel(source)) {
                if (source.isBusinessUnitDefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.isOfficeDefined) {
                    this._office = source.office;
                    this._isOfficeDefined = true;
                }
                else {
                    this._office = '';
                    this._isOfficeDefined = false;
                }
                
                if (source.isFeaturesDefined) {
                    this._features = source.features;
                    this._isFeaturesDefined = true;
                }
                else {
                    this._features = {};
                    this._isFeaturesDefined = true;
                }
            }
            else {
                if (source.businessUnit !== undefined) {
                    this._businessUnit = source.businessUnit;
                    this._isBusinessUnitDefined = true;
                }
                else {
                    this._businessUnit = '';
                    this._isBusinessUnitDefined = false;
                }
                
                if (source.office !== undefined) {
                    this._office = source.office;
                    this._isOfficeDefined = true;
                }
                else {
                    this._office = '';
                    this._isOfficeDefined = false;
                }
                
                if (source.features !== undefined) {
                    this._features = source.features;
                    this._isFeaturesDefined = true;
                }
                else {
                    this._features = {};
                    this._isFeaturesDefined = true;
                }
            }
        }
        else {
            this._businessUnit = '';
            this._isBusinessUnitDefined = false;
        
            this._office = '';
            this._isOfficeDefined = false;
        
            this._features = {};
            this._isFeaturesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return TelemetryContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined)
            definedProperties.push('businessUnit');
        if (this.isOfficeDefined && this.office !== undefined)
            definedProperties.push('office');
        if (this.isFeaturesDefined && this.features !== undefined)
            definedProperties.push('features');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'businessUnit':
                return this.businessUnit;
            case 'office':
                return this.office;
            case 'features':
                return this.features;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            if (this.businessUnit !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.businessUnit);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('businessUnit', 'null'));
            }
        }
        
        if (this.isOfficeDefined && this.office !== undefined) {
            if (this.office !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('office', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.office);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('office', 'null'));
            }
        }
        
        if (this.isFeaturesDefined && this.features !== undefined) {
            if (this.features !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('features', 'dictionary'));
        
                // Implementation: axon/write/dictionary
                writer.writeDictionaryHeader(new Axon.DictionaryHeader(_.keys(this.features).length));
                for (let key in this.features) {
                    const dictionaryValue = this.features[key];
                
                    writer.writeDictionaryItemHeader(new Axon.DictionaryItemHeader('string', dictionaryValue !== null ? 'string' : 'null'));
                
                    // Key Implementation: axon/write/string
                    writer.writeStringValue(key);
                
                    if (dictionaryValue !== null) {
                        // Implementation: axon/write/string
                        writer.writeStringValue(dictionaryValue);
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('features', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'businessUnit':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.businessUnit = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'businessUnit' cannot be null`);
                    this.businessUnit = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'businessUnit'`);
                }
        
                return true;
            case 'office':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.office = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'office' cannot be null`);
                    this.office = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'office'`);
                }
        
                return true;
            case 'features':
                // Implementation: axon/readProperty/dictionary
                if (propertyHeader.type === 'dictionary') {
                    const dictionaryHeader = reader.readDictionaryHeader();
                
                    this.features = {};
                
                    for (let i = 0; i < dictionaryHeader.recordCount; i++) {
                        let key: string = <any>undefined;
                        let value: string = <any>undefined;
                
                        const dictionaryItemHeader = reader.readDictionaryItemHeader();
                
                        // Key Implementation: string
                        if (dictionaryItemHeader.keyType === 'string') {
                            key = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.keyType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'features' cannot be null`);
                            key = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.keyType}' for property 'features'`);
                        }
                
                        // Value Implementation: string
                        if (dictionaryItemHeader.valueType === 'string') {
                            value = reader.readStringValue();
                        }
                        else if (dictionaryItemHeader.valueType === 'null') {
                            // Do Nothing - TODO Review
                            // throw new Error(`Value for property 'features' cannot be null`);
                            value = <any>null;
                        }
                        else {
                            throw new Error(`Unexpected data type '${dictionaryItemHeader.valueType}' for property 'features'`);
                        }
                
                        this.features[key] = value;
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'features' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'features'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // businessUnit
        if (this.isBusinessUnitDefined && this.businessUnit !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.businessUnit === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.businessUnit);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // office
        if (this.isOfficeDefined && this.office !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.office === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.office);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // features
        if (this.isFeaturesDefined && this.features !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/dictionary
            if (this.features === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(_.keys(this.features).length);
            
                for (let key in this.features) {
                    // Key Implementation: string
                    if (key === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(key);
                    }
            
                    // Value Implementation: string
                    if (this.features[key] === null)
                    {
                        writer.writeByteValue(0);
                    }
                    else
                    {
                        writer.writeByteValue(1);
                        writer.writeStringValue(this.features[key]);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // businessUnit
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.businessUnit = <any>null;
                    break;
                case 1:
                    this.businessUnit = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // office
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.office = <any>null;
                    break;
                case 1:
                    this.office = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // features
        if (reader.readBooleanValue())
        {
            // Implementation: dictionary
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null dictionaries');
                    break;
                case 1:
                    let key: string;
                    let value: string;
            
                    this.features = {};
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Key Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                key = <any>null;
                                break;
                            case 1:
                                key = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        // Value Implementation: string
                        switch (reader.readByteValue())
                        {
                            case 0:
                                value = <any>null;
                                break;
                            case 1:
                                value = reader.readStringValue();
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.features[key] = value;
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): TelemetryContext {
        // Method Implementation: simple
        const schema = TelemetryContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: TelemetryContextZodSchemaShape): TelemetryContext {
        // Method Implementation: parseZodSchema
        return new TelemetryContext({
            businessUnit: schema.businessUnit,
            office: schema.office,
            features: schema.features
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            businessUnit: this.isBusinessUnitDefined ? ((this.businessUnit === null ? undefined : this.businessUnit)) : undefined,
            office: this.isOfficeDefined ? ((this.office === null ? undefined : this.office)) : undefined,
            features: this.isFeaturesDefined ? (this.features) : undefined,
        };
    }

    public clone(): TelemetryContext {
        // Method Implementation: clone
        return new TelemetryContext({
            businessUnit: this.isBusinessUnitDefined ? this.businessUnit : undefined,
            office: this.isOfficeDefined ? this.office : undefined,
            features: this.isFeaturesDefined ? this.features : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'EntityLookupParams':
                return new EntityLookupParams();
            case 'QuestionDefault':
                return new QuestionDefault();
            case 'TelemetryContext':
                return new TelemetryContext();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'EntityLookupParams':
                return new EntityLookupParams(source);
            case 'QuestionDefault':
                return new QuestionDefault(source);
            case 'TelemetryContext':
                return new TelemetryContext(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'EntityLookupParams':
                return EntityLookupParams.parse(source);
            case 'QuestionDefault':
                return QuestionDefault.parse(source);
            case 'TelemetryContext':
                return TelemetryContext.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
